import { loadConfig, saveConfig, isConfigured, loadOperatorSession, clearOperatorSession } from '../config.js';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, qs } from '../dom.js';

export async function renderSettings(container: HTMLElement): Promise<void> {
  const config = loadConfig();
  const cameFromConfigured = isConfigured(config);
  const operatorSession = loadOperatorSession();

  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `
    <div class="topbar">
      <h1>Device Settings</h1>
      <button class="ghost" id="homeTop">Home</button>
    </div>
    ${operatorSession ? accountSectionHtml(operatorSession.name) : ''}
    <p class="hint">
      Configured once per device. The Worker URL and token are the same
      ones used to test the API directly — ask whoever deployed the
      Worker if you don't have them. This never touches Mintsoft directly.
    </p>
    <label for="serverUrl">Worker URL</label>
    <input type="text" id="serverUrl" placeholder="https://warehouse-receiving.online-9b0.workers.dev" value="${escapeAttr(config.serverUrl)}" autocapitalize="off" autocorrect="off" spellcheck="false" />

    <label for="appToken">App Token</label>
    <input type="password" id="appToken" placeholder="shared app token" value="${escapeAttr(config.appToken)}" autocapitalize="off" autocorrect="off" spellcheck="false" />

    <p id="status" class="hint"></p>

    <div class="actions-row">
      <button class="ghost" id="test">Test Connection</button>
      <button class="primary" id="save">Save</button>
    </div>
    ${cameFromConfigured ? '<button class="ghost" id="back">Back</button>' : ''}
  `;
  container.appendChild(div);

  const status = qs<HTMLParagraphElement>(div, '#status');
  const serverUrlInput = qs<HTMLInputElement>(div, '#serverUrl');
  const appTokenInput = qs<HTMLInputElement>(div, '#appToken');

  function currentValues() {
    return {
      serverUrl: serverUrlInput.value,
      appToken: appTokenInput.value,
    };
  }

  qs<HTMLButtonElement>(div, '#test').addEventListener('click', async () => {
    status.textContent = 'Checking…';
    status.classList.remove('error-text');
    saveConfig(currentValues());
    try {
      const result = await api.health();
      status.textContent = result.ok ? 'Connected.' : 'Server responded but reported not-ok.';
    } catch (err) {
      status.classList.add('error-text');
      status.textContent = describeError(err);
    }
  });

  qs<HTMLButtonElement>(div, '#save').addEventListener('click', () => {
    const values = currentValues();
    if (!values.serverUrl || !values.appToken) {
      status.classList.add('error-text');
      status.textContent = 'Both fields are required.';
      return;
    }
    saveConfig(values);
    location.hash = '#/';
  });

  div.querySelector<HTMLButtonElement>('#homeTop')?.addEventListener('click', () => {
    location.hash = '#/';
  });

  const back = div.querySelector<HTMLButtonElement>('#back');
  back?.addEventListener('click', () => {
    location.hash = '#/';
  });

  div.querySelector<HTMLButtonElement>('#logout')?.addEventListener('click', () => {
    void logout();
  });

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // Revoking server-side is best-effort — even if this device is
      // offline or the session was already gone, we still want to clear
      // it locally below so the operator is sent back to Login.
    }
    clearOperatorSession();
    location.hash = '#/login';
  }
}

function accountSectionHtml(operatorName: string): string {
  return `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;gap:0.75rem;">
      <div>
        <p class="hint" style="margin:0;">Logged in as</p>
        <p style="margin:0;font-weight:700;">${escapeHtml(operatorName)}</p>
      </div>
      <button class="ghost" id="logout">Log Out</button>
    </div>
  `;
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Could not reach that server — check the URL and your connection.';
  if (err instanceof ApiError) return `${err.message} (${err.status})`;
  return String(err);
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;');
}
